import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addMonths, format, isValid, parseISO } from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import AppointmentDetailDialog from "@/components/appointment/AppointmentDetailDialog";
import AppointmentTable from "@/components/patient/AppointmentTable";
import { Button } from "@/components/ui/button";
import { PAT } from "@/constants/testIds";
import {
  actorDisplay,
  chartReturnPath,
  filterAppointmentsByMonth,
  groupPatientAppointments,
  listAppointments,
  newAppointmentPath,
} from "@/lib/appointmentApi";

const RANGES = [
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
  { id: "all", label: "All" },
];

function todayKey() {
  return format(new Date(), "yyyy-MM-dd");
}

function monthKeyOf(date) {
  return format(date, "yyyy-MM");
}

function formatAppointmentDate(iso) {
  const key = String(iso || "").slice(0, 10);
  const parsed = parseISO(key);
  return isValid(parsed) ? format(parsed, "MMM d, yyyy") : "—";
}

export default function AppointmentsWorkspace({
  patientId,
  patientName,
  encounters = [],
  accessReadOnly = false,
  canWriteEncounters = true,
  canAccessEncounters = true,
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("all");
  const [monthCursor, setMonthCursor] = useState(null);
  const [selected, setSelected] = useState(null);

  const encounterById = useMemo(
    () => Object.fromEntries((encounters || []).map((enc) => [enc.id, enc])),
    [encounters],
  );

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const data = await listAppointments({ patient: patientId });
      const rows = (data.items || []).filter((row) => row.patientId === patientId);
      setItems(rows);
    } catch {
      toast.error("Failed to load appointments.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const monthKey = monthCursor && isValid(monthCursor) ? monthKeyOf(monthCursor) : "";
  const visible = useMemo(() => {
    const scoped = filterAppointmentsByMonth(items, monthKey);
    const grouped = groupPatientAppointments(scoped, todayKey());
    if (range === "upcoming") return { upcoming: grouped.upcoming, past: [] };
    if (range === "past") return { upcoming: [], past: grouped.past };
    return grouped;
  }, [items, monthKey, range]);

  const openNew = () => {
    navigate(
      newAppointmentPath({
        patientId,
        returnTo: chartReturnPath(patientId),
      }),
    );
  };

  const openEncounter = (appointment) => {
    if (!canAccessEncounters || !appointment?.encounterId) return;
    navigate(`/encounters/${encodeURIComponent(appointment.encounterId)}`);
  };

  const dateLabel = monthCursor && isValid(monthCursor) ? format(monthCursor, "MMMM yyyy") : "All dates";
  const hasUpcoming = visible.upcoming.length > 0;
  const hasPast = visible.past.length > 0;

  return (
    <section
      data-testid={PAT.chartAppointmentsPanel}
      className="emr-chart-dense rounded-xl border border-slate-200 bg-[#FFFFFF] p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
    >
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="font-semibold text-slate-900">Appointments</h2>
        {accessReadOnly ? null : (
        <Button
          data-testid={PAT.chartAppointmentsNew}
          className="bg-[#0d5c4d] hover:bg-[#0a4b3f]"
          onClick={openNew}
        >
          <Plus className="h-4 w-4" />
          New Appointment
        </Button>
        )}
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid={PAT.chartAppointmentsPrev}
            onClick={() =>
              setMonthCursor((current) => addMonths(current && isValid(current) ? current : new Date(), -1))
            }
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button variant="outline" size="sm" onClick={() => setMonthCursor(null)} className="min-w-[140px]">
            {dateLabel}
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid={PAT.chartAppointmentsToday}
            onClick={() => setMonthCursor(new Date())}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid={PAT.chartAppointmentsNext}
            onClick={() =>
              setMonthCursor((current) => addMonths(current && isValid(current) ? current : new Date(), 1))
            }
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="inline-flex overflow-hidden rounded-lg border border-slate-200">
          {RANGES.map((item) => (
            <Button
              key={item.id}
              size="sm"
              variant={range === item.id ? "default" : "ghost"}
              data-testid={`${PAT.chartAppointmentsFilter}-${item.id}`}
              className={`rounded-none ${range === item.id ? "bg-slate-800 hover:bg-slate-800" : "text-slate-600"}`}
              onClick={() => setRange(item.id)}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="py-10 text-center text-slate-400">Loading appointments…</p>
      ) : !hasUpcoming && !hasPast ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <CalendarDays className="h-10 w-10 text-slate-300" />
          <p className="text-slate-500">No appointments for this patient in the selected view.</p>
          {accessReadOnly ? null : (
          <Button variant="outline" onClick={openNew}>
            Schedule an appointment
          </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {range !== "past" ? (
            <div data-testid={PAT.chartAppointmentsUpcoming}>
              <h3 className="mb-2 font-semibold uppercase tracking-wide text-slate-500">Upcoming</h3>
              {hasUpcoming ? (
                <AppointmentTable
                  appointments={visible.upcoming}
                  encounterById={encounterById}
                  onOpen={setSelected}
                  onOpenEncounter={openEncounter}
                  canAccessEncounters={canAccessEncounters}
                />
              ) : (
                <p className="text-slate-500">No upcoming appointments.</p>
              )}
            </div>
          ) : null}

          {range !== "upcoming" ? (
            <div data-testid={PAT.chartAppointmentsPast}>
              <h3 className="mb-2 font-semibold uppercase tracking-wide text-slate-500">Past appointments</h3>
              {hasPast ? (
                <AppointmentTable
                  appointments={visible.past}
                  encounterById={encounterById}
                  onOpen={setSelected}
                  onOpenEncounter={openEncounter}
                  canAccessEncounters={canAccessEncounters}
                />
              ) : (
                <p className="text-slate-500">No past appointments.</p>
              )}
            </div>
          ) : null}
        </div>
      )}

      <AppointmentDetailDialog
        appointment={selected}
        patientLabel={patientName || actorDisplay(selected, "Patient/") || "—"}
        locationName={selected ? actorDisplay(selected, "Location/") : ""}
        dateLabel={selected ? formatAppointmentDate(selected.start) : ""}
        onClose={() => setSelected(null)}
        onChanged={load}
        editOptions={{ returnTo: chartReturnPath(patientId) }}
        canWriteAppointments={!accessReadOnly}
        canWriteEncounters={canWriteEncounters}
        canAccessPatients
        canAccessEncounters={canAccessEncounters}
      />
    </section>
  );
}
